import { useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { setTier } from '../lib/license';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

export const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
export const HEARTBEAT_JITTER_MS = 30 * 1000; // ±30s so clients don't sync-stampede
export const EVENT_BEAT_THROTTLE_MS = 30 * 1000; // focus/online beats at most every 30s

/**
 * useSessionHeartbeat — keeps the current session's last_active timestamp
 * fresh on the server, and notifies the caller when the session has been
 * expired (e.g., signed in on another device in the same slot).
 *
 * Cadence: a base beat every ~5 minutes (self-rescheduling setTimeout with
 * ±30s jitter per cycle, so many clients don't all hit the server on the
 * same tick), PLUS an immediate beat on window `focus`, `online`, and
 * `visibilitychange`-to-visible — these catch a laptop waking from sleep or
 * a tab regaining focus without waiting up to 5 minutes for the next tick.
 * Event-triggered beats are throttled to at most one per
 * EVENT_BEAT_THROTTLE_MS so rapid focus/blur churn can't spam the server.
 *
 * Returns { consecutiveFailures } — the current network failure count.
 * ConnectivityBanner subscribes to this to show an "offline" warning.
 *
 * Network errors are swallowed silently — offline grace is handled by
 * ConnectivityBanner's failure-count escalation.
 */
export function useSessionHeartbeat(onSessionExpired) {
  const { user } = useAuth();
  const timeoutRef = useRef(null);
  const callbackRef = useRef(onSessionExpired);
  const lastBeatRef = useRef(0);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);

  // Keep callback ref current without retriggering the effect.
  useEffect(() => {
    callbackRef.current = onSessionExpired;
  }, [onSessionExpired]);

  useEffect(() => {
    if (!user) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setConsecutiveFailures(0);
      return;
    }

    const beat = async () => {
      lastBeatRef.current = Date.now();
      const session = JSON.parse(localStorage.getItem('scrivar_session') || 'null');
      if (!session?.token) return;
      try {
        const res = await fetch(`${API}/auth/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionToken: session.token }),
        });
        const data = await res.json();
        if (data?.valid) {
          setConsecutiveFailures(0);
          if (data.tier && data.tier !== session.tier) {
            localStorage.setItem('scrivar_session', JSON.stringify({ ...session, tier: data.tier }));
            setTier(data.tier);
          }
        } else if (data?.reason) {
          setConsecutiveFailures(0); // server responded — not a connectivity failure
          callbackRef.current?.(data.reason);
        }
      } catch {
        // Network error — increment failure counter, retry next tick
        setConsecutiveFailures((n) => n + 1);
      }
    };

    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled) return;
      const delay = HEARTBEAT_INTERVAL_MS + (Math.random() * 2 - 1) * HEARTBEAT_JITTER_MS;
      timeoutRef.current = setTimeout(async () => {
        await beat();
        if (cancelled) return;
        scheduleNext();
      }, delay);
    };

    beat();
    scheduleNext();

    const maybeBeatFromEvent = () => {
      if (Date.now() - lastBeatRef.current >= EVENT_BEAT_THROTTLE_MS) {
        beat();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        maybeBeatFromEvent();
      }
    };

    window.addEventListener('focus', maybeBeatFromEvent);
    window.addEventListener('online', maybeBeatFromEvent);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      window.removeEventListener('focus', maybeBeatFromEvent);
      window.removeEventListener('online', maybeBeatFromEvent);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  return { consecutiveFailures };
}
